import { useEffect, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import {
  CouponUnavailableReasonEnum,
  EstimateCartResponseDtoExcludedCouponsItem,
  EstimateCartResponseDtoExcludedCouponsItemGroupsItem,
} from '@/apis/rest/generated/types'
import Modal from '@/components/common/modals/Modal'
import { Typo, TypoVariant } from '@/components/common/typography'

interface CartUnApplyCouponModalProps {
  excludedCoupons: EstimateCartResponseDtoExcludedCouponsItem[]
  onConfirm: (couponId: number[]) => void
}

export const CartUnApplyCouponModal = ({ excludedCoupons, onConfirm }: CartUnApplyCouponModalProps) => {
  const { color } = useTheme()
  const [exclusiveCoupons, setExclusiveCoupons] = useState<
    EstimateCartResponseDtoExcludedCouponsItemGroupsItem[] | null
  >(null)

  const unValidCoupons = excludedCoupons
    .filter(
      ({ reason }) =>
        reason === CouponUnavailableReasonEnum.EXPIRED ||
        reason === CouponUnavailableReasonEnum.ALREADY_USED ||
        reason === CouponUnavailableReasonEnum.ONLY_MEMBERSHIP
    )
    .flatMap(({ groups }) => groups.map((coupon) => coupon))

  const handleClickOk = () => {
    const ids = exclusiveCoupons?.map((coupon) => coupon.id)
    setExclusiveCoupons(null)
    if (ids) {
      onConfirm(ids)
    }
  }

  useEffect(() => {
    if (excludedCoupons.length) {
      setExclusiveCoupons(unValidCoupons)
    }
  }, [excludedCoupons.length])

  if (!exclusiveCoupons?.length) {
    return null
  }

  return (
    <Modal open={true}>
      <Modal.Content>
        <Modal.Title>
          <Typo variant={TypoVariant.Heading6Bold}>
            유효하지 않은 쿠폰으로 <br /> 적용 제외되었어요
          </Typo>
        </Modal.Title>
        <Modal.SubTitle>
          <Typo variant={TypoVariant.Body3Regular}>유효기간 만료 및 이미 사용한 쿠폰</Typo>
        </Modal.SubTitle>
        <StyledUnApplyCoupons>
          {exclusiveCoupons?.map(({ name }) => (
            <Typo key={name} as="p" variant={TypoVariant.Body4Regular} color={color.gray700}>
              {name}
            </Typo>
          ))}
        </StyledUnApplyCoupons>
      </Modal.Content>
      <Modal.Footer>
        <ModalButton className="ok" onClick={handleClickOk}>
          <Typo variant={TypoVariant.Body1Bold} color={color.grayWhite}>
            확인
          </Typo>
        </ModalButton>
      </Modal.Footer>
    </Modal>
  )
}

const StyledUnApplyCoupons = styled.div`
  margin-top: 1.6rem;
  border-radius: 8px;
  padding: 1.6rem;
  background-color: ${({
    theme: {
      color: { gray50 },
    },
  }) => gray50};
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  p {
    padding-left: 1rem;
    position: relative;
    gap: 0.6rem;
    &::before {
      position: absolute;
      top: 8px;
      left: 0;
      content: '';
      background-color: ${({
        theme: {
          color: { gray700 },
        },
      }) => gray700};
      width: 0.3rem;
      height: 0.3rem;
    }
  }
`

const ModalButton = styled.button`
  flex: 1;
  border-radius: 0.8rem;
  border: 0.1rem solid;
  padding: 1.3rem 1.6rem;
  cursor: pointer;
  width: 100%;

  &.ok {
    border-color: ${({ theme }) => theme.color.blue500};
    background-color: ${({ theme }) => theme.color.blue500};
  }
`
